/**
 * CritiqueSessionSettingsDialog
 *
 * Dialog for editing critique session settings (feedback options).
 * Feature: 649-critique-feedback-options
 * Extended by: 651-n-comments-task
 */

import { useState, useEffect } from 'react';
import { Modal } from '@/components/composed/Modal';
import { Button } from '@/components/atoms';
import { FeedbackOptionsSelector } from '@/components/composed/FeedbackOptionsSelector';
import type { FeedbackType, NCommentsConfig } from '@/types/photo-rating';
import logger from '@/utils/logger';

export interface CritiqueSessionSettingsDialogProps {
  open: boolean;
  onClose: () => void;
  /** Feature 651: Extended to include nCommentsConfig when n_comments is enabled */
  onSave: (feedbackOptions: FeedbackType[], nCommentsConfig?: NCommentsConfig) => Promise<void>;
  sessionTitle: string;
  currentFeedbackOptions: FeedbackType[];
  /** Feature 651: Current N Comments config */
  currentNCommentsConfig?: NCommentsConfig;
  /** Feature 651: Number of photos in collection (required for n_comments option) */
  photoCount?: number;
  isSaving?: boolean;
}

export function CritiqueSessionSettingsDialog({
  open,
  onClose,
  onSave,
  sessionTitle,
  currentFeedbackOptions,
  currentNCommentsConfig,
  photoCount,
  isSaving = false,
}: CritiqueSessionSettingsDialogProps) {
  // Always create a copy of the array to avoid mutating read-only props
  const [feedbackOptions, setFeedbackOptions] = useState<FeedbackType[]>(() => [...currentFeedbackOptions]);
  const [nCommentsConfig, setNCommentsConfig] = useState<NCommentsConfig | undefined>(currentNCommentsConfig);

  // Reset form when dialog opens with current values
  useEffect(() => {
    if (open) {
      setFeedbackOptions([...currentFeedbackOptions]);
      setNCommentsConfig(currentNCommentsConfig);
    }
  }, [open, currentFeedbackOptions, currentNCommentsConfig]);

  const handleSave = async () => {
    if (feedbackOptions.length === 0) {
      return;
    }
    // Include nCommentsConfig only if n_comments is enabled
    const config = feedbackOptions.includes('n_comments') ? nCommentsConfig : undefined;
    logger.info('UI', 'Saving critique session settings', { feedbackOptions, nCommentsConfig: config });
    await onSave(feedbackOptions, config);
  };

  const isValid = feedbackOptions.length > 0;
  // Create copies before sorting to avoid mutating read-only arrays
  const hasTypeChanges = JSON.stringify([...feedbackOptions].sort()) !== JSON.stringify([...currentFeedbackOptions].sort());
  const hasNCommentsChanges = nCommentsConfig?.targetCount !== currentNCommentsConfig?.targetCount;
  const hasChanges = hasTypeChanges || hasNCommentsChanges;

  const footer = (
    <div className="flex justify-end gap-2">
      <Button variant="secondary" onClick={onClose} disabled={isSaving}>
        Cancel
      </Button>
      <Button
        variant="primary"
        onClick={handleSave}
        disabled={!isValid || !hasChanges || isSaving}
      >
        {isSaving ? 'Saving...' : 'Save Settings'}
      </Button>
    </div>
  );

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Session Settings"
      size="md"
      footer={footer}
    >
      <div className="space-y-4">
        <div className="text-sm text-muted-foreground">
          Configure feedback options for <span className="font-medium text-foreground">{sessionTitle}</span>
        </div>

        {/* Feature 651: Extended with N Comments support */}
        <FeedbackOptionsSelector
          value={feedbackOptions}
          onChange={setFeedbackOptions}
          nCommentsConfig={nCommentsConfig}
          onNCommentsConfigChange={setNCommentsConfig}
          photoCount={photoCount}
          disabled={isSaving}
          error={feedbackOptions.length === 0 ? 'At least one feedback option is required' : undefined}
        />
      </div>
    </Modal>
  );
}

CritiqueSessionSettingsDialog.displayName = 'CritiqueSessionSettingsDialog';
